import React from 'react'

const UserLogsStyle = () => {
    return (
        <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@300;400;500;600;700&family=Space+Grotesk:wght@400;500;600;700&display=swap');

        :root {
          --term-bg: #0a0e14;
          --term-surface: #0d1117;
          --term-border: #1b2332;
          --term-gutter: #161b22;
          --term-text: #c9d1d9;
          --term-muted: #484f58;
          --term-cyan: #22d3ee;
          --term-green: #34d399;
          --term-yellow: #fbbf24;
          --term-red: #f87171;
          --term-purple: #a78bfa;
          --term-blue: #60a5fa;
          --term-prompt: #34d399;
          --font-mono: 'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace;
        }

        * { margin: 0; padding: 0; box-sizing: border-box; }

        .log-root {
          min-height: 100vh;
          background: var(--term-bg);
          color: var(--term-text);
          font-family: var(--font-mono);
          position: relative;
          overflow-x: hidden;
        }

        /* Scanline overlay */
        .log-root::before {
          content: '';
          position: fixed;
          inset: 0;
          background: repeating-linear-gradient(
            0deg,
            transparent,
            transparent 2px,
            rgba(0,0,0,0.03) 2px,
            rgba(0,0,0,0.03) 4px
          );
          pointer-events: none;
          z-index: 1000;
        }

        /* Subtle CRT vignette */
        .log-root::after {
          content: '';
          position: fixed;
          inset: 0;
          background: radial-gradient(ellipse at center, transparent 60%, rgba(0,0,0,0.4) 100%);
          pointer-events: none;
          z-index: 999;
        }

        .log-container {
          max-width: 1440px;
          margin: 0 auto;
          padding: 20px 24px;
        }

        /* ─── HEADER ─── */
        .log-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          background: var(--term-surface);
          border: 1px solid var(--term-border);
          border-radius: 8px 8px 0 0;
          margin-bottom: 0;
        }

        .log-header-left {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .log-header-dots {
          display: flex;
          gap: 8px;
        }
        .log-header-dots span {
          width: 12px; height: 12px;
          border-radius: 50%;
          display: block;
        }
        .dot-red { background: #ff5f57; }
        .dot-yellow { background: #ffbd2e; }
        .dot-green { background: #28ca41; }

        .log-title-block h1 {
          font-size: 14px;
          font-weight: 600;
          color: var(--term-text);
          letter-spacing: 0.5px;
        }
        .log-title-block span {
          font-size: 11px;
          color: var(--term-muted);
        }

        .log-header-right {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .log-clock {
          font-size: 13px;
          color: var(--term-cyan);
          font-weight: 500;
          letter-spacing: 1px;
        }

        .live-badge {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          cursor: pointer;
          border: none;
          transition: all 0.3s ease;
        }
        .live-badge.active {
          background: rgba(52, 211, 153, 0.12);
          color: var(--term-green);
          border: 1px solid rgba(52, 211, 153, 0.25);
        }
        .live-badge.inactive {
          background: rgba(248, 113, 113, 0.1);
          color: var(--term-red);
          border: 1px solid rgba(248, 113, 113, 0.2);
        }
        .live-dot {
          width: 7px; height: 7px;
          border-radius: 50%;
          animation: livePulse 2s ease-in-out infinite;
        }
        .live-badge.active .live-dot { background: var(--term-green); }
        .live-badge.inactive .live-dot { background: var(--term-red); animation: none; }

        @keyframes livePulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 4px var(--term-green); }
          50% { opacity: 0.3; box-shadow: none; }
        }

        /* ─── TERMINAL MINI ─── */
        .mini-terminal {
          background: var(--term-bg);
          border-left: 1px solid var(--term-border);
          border-right: 1px solid var(--term-border);
          padding: 12px 20px;
          max-height: 120px;
          overflow-y: auto;
          font-size: 11px;
          line-height: 1.7;
          color: var(--term-muted);
          scrollbar-width: thin;
          scrollbar-color: var(--term-border) transparent;
        }
        .mini-terminal::-webkit-scrollbar { width: 4px; }
        .mini-terminal::-webkit-scrollbar-thumb { background: var(--term-border); border-radius: 2px; }

        .term-line {
          animation: termFadeIn 0.3s ease;
        }
        .term-line .term-time {
          color: var(--term-muted);
          margin-right: 8px;
        }
        .term-line .term-prompt {
          color: var(--term-green);
          margin-right: 4px;
        }
        .term-line .term-text {
          color: var(--term-text);
        }

        @keyframes termFadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* ─── STATS BAR ─── */
        .stats-bar {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1px;
          background: var(--term-border);
          border-left: 1px solid var(--term-border);
          border-right: 1px solid var(--term-border);
        }
        .stat-item {
          background: var(--term-surface);
          padding: 14px 20px;
          display: flex;
          align-items: center;
          gap: 12px;
          transition: background 0.2s ease;
        }
        .stat-item:hover {
          background: var(--term-gutter);
        }
        .stat-icon {
          width: 36px; height: 36px;
          border-radius: 6px;
          display: flex; align-items: center; justify-content: center;
          font-size: 18px; font-weight: 700;
        }
        .stat-info .stat-count {
          font-size: 20px; font-weight: 700; line-height: 1;
        }
        .stat-info .stat-label {
          font-size: 10px; color: var(--term-muted);
          text-transform: uppercase; letter-spacing: 1.5px; margin-top: 2px;
        }

        /* ─── FILTER BAR ─── */
        .filter-bar {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: var(--term-gutter);
          border-left: 1px solid var(--term-border);
          border-right: 1px solid var(--term-border);
          border-top: 1px solid var(--term-border);
          flex-wrap: wrap;
        }

        .filter-prefix {
          color: var(--term-green);
          font-weight: 600;
          font-size: 12px;
          margin-right: 4px;
        }

        .filter-input {
          background: var(--term-bg);
          border: 1px solid var(--term-border);
          color: var(--term-text);
          padding: 6px 12px;
          border-radius: 4px;
          font-family: var(--font-mono);
          font-size: 12px;
          outline: none;
          transition: border-color 0.2s;
        }
        .filter-input:focus {
          border-color: var(--term-cyan);
          box-shadow: 0 0 0 2px rgba(34,211,238,0.1);
        }
        .filter-input::placeholder { color: var(--term-muted); }

        .filter-select {
          background: var(--term-bg);
          border: 1px solid var(--term-border);
          color: var(--term-text);
          padding: 6px 12px;
          border-radius: 4px;
          font-family: var(--font-mono);
          font-size: 12px;
          outline: none;
          cursor: pointer;
          appearance: none;
          -webkit-appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23484f58' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 8px center;
          padding-right: 28px;
        }
        .filter-select:focus {
          border-color: var(--term-cyan);
        }
        .filter-select option {
          background: var(--term-bg);
          color: var(--term-text);
        }

        .filter-btn {
          background: transparent;
          border: 1px solid var(--term-border);
          color: var(--term-text);
          padding: 6px 14px;
          border-radius: 4px;
          font-family: var(--font-mono);
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .filter-btn:hover {
          background: var(--term-border);
        }
        .filter-btn.primary {
          background: rgba(34,211,238,0.1);
          border-color: rgba(34,211,238,0.3);
          color: var(--term-cyan);
        }
        .filter-btn.primary:hover {
          background: rgba(34,211,238,0.2);
        }

        .filter-sep {
          width: 1px;
          height: 24px;
          background: var(--term-border);
          margin: 0 4px;
        }

        /* ─── LOG TABLE ─── */
        .log-table-wrap {
          border-left: 1px solid var(--term-border);
          border-right: 1px solid var(--term-border);
          overflow-x: auto;
          scrollbar-width: thin;
          scrollbar-color: var(--term-border) transparent;
        }

        .log-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }

        .log-table thead {
          background: var(--term-gutter);
          position: sticky;
          top: 0;
          z-index: 10;
        }
        .log-table thead th {
          padding: 10px 16px;
          text-align: left;
          font-weight: 500;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          color: var(--term-muted);
          border-bottom: 1px solid var(--term-border);
          white-space: nowrap;
        }

        .log-table tbody tr {
          border-bottom: 1px solid rgba(27, 35, 50, 0.5);
          transition: all 0.15s ease;
          cursor: pointer;
          opacity: 0;
          transform: translateX(-10px);
        }
        .log-table tbody tr.visible {
          opacity: 1;
          transform: translateX(0);
          transition: opacity 0.4s ease, transform 0.4s ease, background 0.15s ease;
        }
        .log-table tbody tr:hover {
          background: rgba(34, 211, 238, 0.03);
        }
        .log-table tbody tr.selected {
          background: rgba(34, 211, 238, 0.06);
          border-left: 2px solid var(--term-cyan);
        }

        .log-table td {
          padding: 10px 16px;
          white-space: nowrap;
          vertical-align: middle;
        }

        .log-line-num {
          color: var(--term-muted);
          font-size: 10px;
          min-width: 32px;
          text-align: right;
          padding-right: 12px !important;
          user-select: none;
          border-right: 1px solid var(--term-border);
        }

        .log-time {
          color: var(--term-muted);
          font-size: 11px;
        }

        .log-user {
          color: var(--term-cyan);
          font-weight: 500;
        }

        .log-email {
          color: var(--term-muted);
          font-size: 11px;
        }

        .action-tag {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 2px 10px;
          border-radius: 3px;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
        .action-icon {
          font-size: 14px;
          font-weight: 700;
          line-height: 1;
        }

        .log-by {
          color: var(--term-purple);
          font-size: 11px;
        }

        .log-profil {
          color: var(--term-text);
          font-size: 11px;
        }

        .log-agence {
          color: var(--term-muted);
          font-size: 11px;
        }

        .status-dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          display: inline-block;
          margin-right: 6px;
        }
        .status-active { background: var(--term-green); box-shadow: 0 0 6px rgba(52,211,153,0.4); }
        .status-inactive { background: var(--term-red); }

        /* ─── DETAIL PANEL ─── */
        .detail-panel {
          background: var(--term-surface);
          border-left: 1px solid var(--term-border);
          border-right: 1px solid var(--term-border);
          border-top: 1px solid var(--term-border);
          padding: 16px 20px;
          animation: panelSlide 0.3s ease;
        }

        @keyframes panelSlide {
          from { opacity: 0; max-height: 0; padding: 0 20px; }
          to { opacity: 1; max-height: 300px; }
        }

        .detail-title {
          font-size: 11px;
          color: var(--term-cyan);
          text-transform: uppercase;
          letter-spacing: 2px;
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .detail-close {
          background: none; border: none;
          color: var(--term-muted); cursor: pointer;
          font-family: var(--font-mono); font-size: 14px;
          transition: color 0.2s;
        }
        .detail-close:hover { color: var(--term-text); }

        .detail-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 12px;
        }
        .detail-field label {
          display: block;
          font-size: 9px;
          color: var(--term-muted);
          text-transform: uppercase;
          letter-spacing: 1.5px;
          margin-bottom: 3px;
        }
        .detail-field span {
          font-size: 12px;
          color: var(--term-text);
        }

        /* ─── PAGINATION ─── */
        .log-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 20px;
          background: var(--term-surface);
          border: 1px solid var(--term-border);
          border-radius: 0 0 8px 8px;
        }
        .page-info {
          font-size: 11px;
          color: var(--term-muted);
        }
        .page-controls {
          display: flex;
          gap: 4px;
        }
        .page-btn {
          background: transparent;
          border: 1px solid var(--term-border);
          color: var(--term-muted);
          padding: 4px 10px;
          border-radius: 3px;
          font-family: var(--font-mono);
          font-size: 11px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .page-btn:hover:not(:disabled) {
          background: var(--term-border);
          color: var(--term-text);
        }
        .page-btn.active {
          background: rgba(34,211,238,0.1);
          border-color: rgba(34,211,238,0.3);
          color: var(--term-cyan);
        }
        .page-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        /* Loading */
        .log-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 40px;
          color: var(--term-muted);
          font-size: 12px;
        }
        .loading-spinner {
          display: inline-block;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .log-empty {
          text-align: center;
          padding: 40px;
          color: var(--term-muted);
          font-size: 12px;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .log-container { padding: 12px; }
          .stats-bar { grid-template-columns: repeat(2, 1fr); }
          .filter-bar { flex-direction: column; align-items: stretch; }
          .filter-sep { display: none; }
          .log-header { flex-direction: column; gap: 12px; align-items: flex-start; }
          .log-header-right { width: 100%; justify-content: space-between; }
        }
      `}</style>
    )
}

export default UserLogsStyle